/// <amd-module name="Pipeline/Analytics/Common/Sources/PipelineReportSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { AnalyticsODataClientBase } from "Analytics/Shared/Common/Sources/AnalyticsODataClientBase";
import { ContextType, IReportContext } from "Analytics/Shared/Common/Types";
import { getDateFilter } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { IFilterItem, ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { getPercentage } from "Analytics/Shared/Utils/Number";
import * as MockData from "Pipeline/Analytics/Common/MockReportData";
import * as Resources from "Pipeline/Analytics/Common/Resources";
import { IStageMetrics } from "Pipeline/Analytics/Common/Types";
import { format } from "VSS/Core/Util/String";

import { BuildOutcome, BuildTaskOutcome, PullRequestBranchPlaceHolderSK, PullRequestBranchPrefix } from "../Types";

export abstract class PipelineReportSource extends AnalyticsODataClientBase {
    protected pipelineEntityEmptyNamePrefix: string = "_generatedDefaultForEmpty.";
    protected _buildEntityName: string = PipelineEntities.PipelineRuns;
    protected _buildTaskResultsEntityName: string = PipelineEntities.PipelineRunActivityResults;
    protected _defaultStage: string = "__default";
    protected _emptyString = "";

    protected _getTotalCountForAggregation(): string {
        return "$count as TotalCount";
    }

    protected _getGroupByTask(): string {
        return "TaskDisplayName";
    }

    protected _getGroupByBuildId(): string {
        return PipelineEntities.PipelineRunId;
    }

    protected _getDurationPercentile(): string {
        return "DurationPercentile";
    }

    protected _getTotalDurationSeconds(): string {
        return "TotalDurationSeconds";
    }

    protected _getActivityDurationSeconds(): string {
        return "ActivityDurationSeconds";
    }

    protected _getGroupByBranch(): string {
        return "BranchSK, Branch/BranchName";
    }

    protected _getGroupByCompletedDate(): string {
        return "CompletedDateSK";
    }

    protected _getBuildCompletedDateSK(): string {
        return PipelineEntities.PipelineRunCompletedDateSK;
    }

    protected _getPipelineJobStageName(): string {
        return "PipelineJob/StageName";
    }

    protected _getCountForAggregationAsFailedCount(): string {
        return "$count as FailedCount";
    }

    protected _getFailedCountForAggregation(): string {
        return "FailedCount with sum as FailedCount";
    }

    protected _getSucceededCountForAggregation(): string {
        return "SucceededCount with sum as SucceededCount";
    }

    protected _getPartiallySucceededCountForAggregation(): string {
        return "PartiallySucceededCount with sum as PartiallySucceededCount";
    }

    protected _getTotalDurationForAggregation(): string {
        return "TotalDurationSeconds with sum as TotalDuration";
    }

    protected _getActivityTotalDurationForAggregation(): string {
        return "ActivityDurationSeconds with sum as TotalDuration";
    }

    protected _getDurationPercentileForAggregation(): string {
        return "DurationPercentile with sum as TotalDuration";
    }

    protected _getFailedStageCountForAggregation(): string {
        return "cast(FailedCount gt 0,  Edm.Int32) with sum as FailedStageCount";
    }

    protected _getDurationPercentileForCompute(percentile_cont: string): string {
        return "percentile_cont(TotalDurationSeconds, " + percentile_cont + ") as DurationPercentile";
    }

    protected _getTaskDurationPercentileForCompute(percentile_cont: string): string {
        return "percentile_cont(ActivityDurationSeconds, " + percentile_cont + ") as DurationPercentile";
    }

    protected _getDurationPercentileWithGroupByForCompute(percentile_cont: string, group: string, groupBy: string): string {
        return format("percentile_cont({0}, {1}, {2}) as DurationPercentile", group, percentile_cont, groupBy);
    }

    protected _getPipelineContextFilter(reportContext: IReportContext, takeJoin: boolean = false): string {
        let queryField = "";
        let queryValue = 0;

        switch (reportContext.contextType) {
            case ContextType.Build:
                if (reportContext.definitionSK) {
                    queryField = PipelineEntities.PipelineSK;
                    queryValue = reportContext.definitionSK;
                } else {
                    queryField = takeJoin ? `${PipelineEntities.Pipeline}/${PipelineEntities.PipelineId}` : PipelineEntities.PipelineId;
                    queryValue = reportContext.definitionId;
                }
                break;
        }

        return `${queryField} eq ${queryValue}`;
    }

    protected _getBranchFilter(branchItems?: IFilterItem[]): string {
        if (!branchItems || branchItems.length == 0) {
            return "";
        }

        const branchPredicates: string[] = branchItems.map(branchItem => {
            if (branchItem.id === PullRequestBranchPlaceHolderSK) {
                return `startswith(Branch/BranchName, '${PullRequestBranchPrefix}')`;
            } else {
                return `BranchSK eq ${branchItem.id}`;
            }
        });

        return ` and (${branchPredicates.join(" or ")})`;
    }

    protected _getStageFilter(stageName?: string): string {
        return stageName ? `and (PipelineJob/StageName eq '${stageName}')` : "";
    }

    protected _parseTaskDisplayName(record: any): string {
        if (record) {
            return record.TaskDisplayName;
        } else {
            return "";
        }
    }

    protected _parseBuildId(record: any): string {
        if (record) {
            return record[PipelineEntities.PipelineRunId];
        } else {
            return "";
        }
    }

    protected _parseTotalCount(record: any): number {
        return (record.TotalCount as number) || 0;
    }

    protected _parseSucceededCount(record: any): number {
        return (record.SucceededCount as number) || 0;
    }

    protected _parseFailedCount(record: any): number {
        return (record.FailedCount as number) || 0;
    }

    protected _parsePartiallySucceededCount(record: any): number {
        return (record.PartiallySucceededCount as number) || 0;
    }

    protected _parseTotalDuration(record: any): number {
        return (record.TotalDuration as number) || 0;
    }

    protected _parseDurationPercentile(record: any): number {
        return (record.DurationPercentile as number) || 0;
    }

    protected _parseCompletedDateSK(record: any): number {
        return (record.CompletedDateSK as number) || 0;
    }

    protected _parseBuildCompletedDateSK(record: any): number {
        return (record[PipelineEntities.PipelineRunCompletedDateSK] as number) || 0;
    }

    protected _parseStageFailureCount(record: any): number {
        return (record.FailedStageCount as number) || 0;
    }

    protected _parsePipelineStageName(record: any): string {
        return record.PipelineJob.StageName;
    }

    protected _getTotalPipelineFilter(): string {
        return "(CanceledCount ne 1)";
    }

    protected _getTotalPipelineFilterForTask(): string {
        return `(${PipelineEntities.PipelineRunOutcome} ne 'Canceled')`;
    }

    protected _getSuccessfulPipelineFilter(): string {
        return "(SucceededCount eq 1 or PartiallySucceededCount eq 1)";
    }

    protected _getFailedPipelineFilter(): string {
        return "(FailedCount eq 1)";
    }

    protected _getTaskActivityQueryFilter(): string {
        // We only want activity of type 'Task'. Before ActivityType column was added, default value is NULL for task type activity.
        return " (ActivityType eq null or ActivityType eq 'Task') ";      
    }

    protected _getBasicFilters(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): string {
        return `${this._getPipelineContextFilter(reportContext, true)} and 
                ${getDateFilter(confValues.trendBy, confValues.period, PipelineEntities.PipelineRunCompletedDateSK)} 
                ${this._getBranchFilter(confValues.branchItems)} 
                and ${this._getTaskActivityQueryFilter()}`;
    }

    protected _getFailingTaskFilters(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): string {
        return `${this._getBasicFilters(reportContext, confValues)} and  
                ${PipelineEntities.PipelineRunOutcome} eq '${BuildOutcome.Failed}' and
                TaskOutcome eq '${BuildTaskOutcome.Failed}'`;
    }

    protected _getBasicQueryFilter(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): string {
        return `filter(${this._getBasicFilters(reportContext, confValues)})`;
    }

    protected _getTopFailingTaskQueryFilter(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): string {
        return `filter(${this._getFailingTaskFilters(reportContext, confValues)})`;
    }

    protected _getFailedPipelineRunsForTaskQueryFilter(
        reportContext: IReportContext,
        confValues: ITrendChartReportConfiguration,
        taskDisplayName: string,
        stage: string | undefined
    ): string {
        return `filter(${this._getFailingTaskFilters(reportContext, confValues)} 
        ${this._getStageFilter(stage)} and TaskDisplayName eq '${taskDisplayName}')`;
    }

    protected _getTopFailingTaskQueryFilterForStage(
        reportContext: IReportContext,
        confValues: ITrendChartReportConfiguration,
        stage: string
    ): string {
        if (stage) {
            return `filter(${this._getFailingTaskFilters(reportContext, confValues)} and PipelineJob/StageName eq '${stage}')`;
        } else {
            return `filter(${this._getFailingTaskFilters(reportContext, confValues)} 
                    and (PipelineJob/StageName eq null 
                    or PipelineJob/StageName eq '${this._defaultStage}'))`;
        }
    }

    protected _getMaxDurationTaskQueryFilter(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): string {
        return `filter(${this._getPipelineContextFilter(reportContext, true)} and 
                ${getDateFilter(confValues.trendBy, confValues.period, PipelineEntities.PipelineRunCompletedDateSK)}
                ${this._getBranchFilter(confValues.branchItems)} and
                (${PipelineEntities.PipelineRunOutcome} eq '${BuildOutcome.Succeed}' or 
                ${PipelineEntities.PipelineRunOutcome} eq '${BuildOutcome.PartiallySucceeded}') and 
                (CanceledCount ne 1 and SkippedCount ne 1 and AbandonedCount ne 1) and
                ${this._getTaskActivityQueryFilter()})`;
    }

    protected _getPassPercentage(totalCount: number, succeededCount: number, partiallySucceededCount: number): number {
        const passedCount: number = succeededCount + partiallySucceededCount;
        return getPercentage(totalCount, passedCount);
    }

    protected _getPercentageVariation(prevValue: number, currentValue: number): number {
        const diff: number = Math.abs(prevValue - currentValue);
        return getPercentage(prevValue, diff);
    }

    protected _parseStageData(stageName: string): IStageMetrics {
        let stageMetrics: IStageMetrics;
        if (!stageName || stageName === this._defaultStage) {
            stageMetrics = {
                name: Resources.DefaultStageName,
                fullName: "",
                dataCount: 0
            };
        } else {
            stageMetrics = {
                name: stageName.replace(this.pipelineEntityEmptyNamePrefix, ""),
                fullName: stageName,
                dataCount: 0
            };
        }
        return stageMetrics;
    }

    protected _parseStageName(stageName: string): string {
        let stageDisplayName: string;
        if (!stageName || stageName === this._defaultStage) {
            stageDisplayName = Resources.DefaultStageName;
        } else {
            stageDisplayName = stageName.replace(this.pipelineEntityEmptyNamePrefix, "");
        }
        return stageDisplayName;
    }

    protected getStages(
        reportContext: IReportContext,
        confValues: ITrendChartReportConfiguration,
        getOnlyFailedStages = false
    ): Promise<IStageMetrics[]> {
        const projectId = this._getProjectId();
        const queryOptions = {
            entityType: this._buildEntityName,
            project: projectId
        } as ODataQueryOptions;

        if (MockData.useMockData) {
            return Promise.resolve(MockData.getMockStageData());
        }

        queryOptions.entityType = this._buildTaskResultsEntityName;
        const filterStr = getOnlyFailedStages
            ? this._getTopFailingTaskQueryFilter(reportContext, confValues)
            : this._getBasicQueryFilter(reportContext, confValues);
        const groupByPrimaryStr = `groupby((${this._getGroupByBuildId()}, ${this._getPipelineJobStageName()}), aggregate(${this._getFailedCountForAggregation()}))`;
        const groupBySecondaryStr = `groupby((${this._getPipelineJobStageName()}), aggregate(${this._getFailedStageCountForAggregation()}))`;
        queryOptions.$apply = `${filterStr}/${groupByPrimaryStr}/${groupBySecondaryStr}`;
        return this._queryStages(queryOptions);
    }

    protected async _queryStages(queryOptions: ODataQueryOptions): Promise<IStageMetrics[]> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("data is empty.");
        }

        const dataValues = data.value as any[];
        const stageMetricsList: { [key: string]: IStageMetrics } = {};

        if (dataValues && dataValues.length > 0) {
            dataValues.forEach(d => {
                const stageName = this._parsePipelineStageName(d);
                const stageMetrics = this._parseStageData(stageName);
                if (!stageMetricsList[stageMetrics.fullName]) {
                    stageMetricsList[stageMetrics.fullName] = {
                        name: stageMetrics.name,
                        fullName: stageMetrics.fullName,
                        dataCount: 0
                    };
                }
                stageMetricsList[stageMetrics.fullName].dataCount += this._parseStageFailureCount(d);
            });
        }
        const allStagesData: IStageMetrics[] = Object.keys(stageMetricsList).map(stageData => {
            return stageMetricsList[stageData];
        });

        return allStagesData;
    } 
}
